import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { PlayerInterface } from '@frameio/media-viewer/hooks/usePlayer/types';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useMediaData } from '@frameio/shared/contexts/MediaViewerDataContext';
import { useActiveViewableAssetId } from '@frameio/shared/hooks';
import { useGetActiveSequenceMetadataQuery } from '@frameio/shared/integrations/gql';
import type { MediaDataType } from '@frameio/shared/transfers/types';
import { getBrowserSafeSeconds } from '@frameio/utils/formatters';
import {
  convertSecondsToMicroseconds,
  getFrameOffset,
} from '@frameio/utils/media/media';
import { useActiveDocumentId } from '../../../shared/contexts/ActiveHostEntitiesContext';
import { useDatadogLogger } from '../../../shared/hooks/useDatadogLogger';
import { usePluginFeatures } from '../../../shared/hooks/usePluginFeatures';
import { POINT_NOT_SET_VALUE } from '../../constants';
import type { PlayheadPositionChangedSubscription } from '../../gql';
import {
  useGetPlayheadSyncStatusQuery,
  usePlayheadPositionChangedSubscription,
  useSetPlayheadPositionMutation,
} from '../../gql';

/* Precision threshold to prevent echo loops from floating-point rounding during
 * unit conversions */
const POSITION_THRESHOLD_MICROSECONDS = 10;
const MICROSECONDS_PER_SECOND = 1000000;

/**
 * Converts Premiere's sequence-relative microsecond position to frame-accurate
 * player-relative seconds by subtracting the asset's sequence in-point offset,
 * rounding to the nearest frame, and applying frame offset.
 *
 * @param position - The playhead position payload from Premiere containing
 * positionMicroseconds (sequence-relative position)
 * @param mediaData - The media data containing frame rate and type information
 * @param sequenceInOffsetMicroseconds - The asset's in-point in the sequence,
 * in microseconds
 * @returns The frame-accurate player-relative time in seconds
 */
const snapToFrameAccurateTime = (
  position: PlayheadPositionChangedSubscription['playheadPositionChanged'],
  mediaData: MediaDataType | null,
  sequenceInOffsetMicroseconds: number
): number => {
  const relativeMicroseconds =
    position.positionMicroseconds - sequenceInOffsetMicroseconds;
  let positionSeconds = relativeMicroseconds / MICROSECONDS_PER_SECOND;

  const frameRate =
    mediaData?.__typename === 'VideoMedia' ? mediaData?.fps : null;

  if (frameRate) {
    const frameNumber = Math.round(positionSeconds * frameRate);
    const browserSafeSeconds = getBrowserSafeSeconds(
      frameNumber / frameRate + getFrameOffset(frameRate)
    );
    positionSeconds = browserSafeSeconds ?? positionSeconds;
  }

  return positionSeconds;
};

/**
 * Returns true if positions differ by less than the precision threshold,
 * preventing echo loops from floating-point rounding errors.
 *
 * @param currentMicroseconds - The current playhead position in microseconds
 * @param lastSyncedMicroseconds - The last synced position in microseconds
 * @returns true if positions are within threshold (should skip sync), false
 * otherwise
 */
const isPositionWithinThreshold = (
  currentMicroseconds: number,
  lastSyncedMicroseconds: number
): boolean => {
  return (
    Math.abs(currentMicroseconds - lastSyncedMicroseconds) <
    POSITION_THRESHOLD_MICROSECONDS
  );
};

/**
 * Returns true when an echo should be suppressed: the last synced position from
 * Premiere was outside the video range and the player's currentTime is still on
 * the clamped boundary frame (start or end). Returns false when the user has
 * seeked away from the boundary, allowing normal sync to proceed.
 */
const shouldSuppressOutOfBoundsEcho = (
  currentTime: number | null | undefined,
  lastSyncedPosition: number | null,
  mediaData: MediaDataType | null
): boolean => {
  if (lastSyncedPosition === null) {
    return false;
  }

  const isVideo = mediaData?.__typename === 'VideoMedia';
  const duration = isVideo ? mediaData.duration : null;
  const fps = (isVideo ? mediaData.fps : null) ?? 24;
  const frameSeconds = 1 / fps;

  const wasOutOfBounds =
    lastSyncedPosition < 0 || (duration && lastSyncedPosition > duration);

  if (!wasOutOfBounds) {
    return false;
  }

  return !currentTime || !!(duration && currentTime >= duration - frameSeconds);
};

/**
 * Hook to synchronize playhead position between Frame.io and Premiere Pro
 *
 * Handles bidirectional sync:
 * - Premiere → Frame.io: Subscribes to playhead position updates from Premiere
 *   via GraphQL subscription
 * - Frame.io → Premiere: Watches playerInterface.current.currentTimeLastUpdated
 *   and sends position via GraphQL mutation
 *
 * @param playerInterface - The player interface to synchronize playhead
 * position
 * @returns An object containing the sync status
 */
export const usePremierePlayheadSync = ({
  playerInterface,
}: {
  playerInterface: PlayerInterface;
}) => {
  const logger = useDatadogLogger();
  const isWebClient = useIsMatchingClientName('web');
  const assetId = useActiveViewableAssetId();
  const sequenceId = useActiveDocumentId();
  const mediaData = useMediaData();
  const { canSyncPlayhead } = usePluginFeatures();

  const lastSyncedPositionRef = useRef<number | null>(null);
  const skipNextSyncRef = useRef(false);

  const skipQuery = isWebClient || !assetId || !sequenceId || !canSyncPlayhead;

  const { data: sequenceMetadataData } = useGetActiveSequenceMetadataQuery({
    context: { resolveWithUXP: true },
    skip: skipQuery,
  });

  const sequenceInOffsetMicroseconds = useMemo(() => {
    const metadata =
      sequenceMetadataData?.activeProject?.activeSequence?.metadata;

    if (!metadata || !assetId) {
      return 0;
    }

    const exportMeta = metadata.find(
      (item) =>
        item?.__typename === 'SequenceExportsXMPMetadata' &&
        item.assetId === assetId
    );

    if (
      !exportMeta ||
      !('sequenceIn' in exportMeta) ||
      !exportMeta.sequenceIn ||
      exportMeta.sequenceIn === POINT_NOT_SET_VALUE
    ) {
      return 0;
    }

    return convertSecondsToMicroseconds(exportMeta.sequenceIn) ?? 0;
  }, [sequenceMetadataData, assetId]);

  const { data: syncStatus, error: syncStatusError } =
    useGetPlayheadSyncStatusQuery({
      context: { resolveWithUXP: true },
      skip: skipQuery,
      variables: { assetId, sequenceId },
    });

  useEffect(() => {
    if (!syncStatusError) return;
    logger.error('GetPlayheadSyncStatus query failed', {
      error: syncStatusError.message,
      assetId,
      sequenceId,
    });
  }, [syncStatusError, assetId, sequenceId, logger]);

  const isSyncEnabled = syncStatus?.getPlayheadSyncStatus?.status === 'ENABLED';
  const shouldSkip = !isSyncEnabled || skipQuery;

  const [setPlayheadPosition] = useSetPlayheadPositionMutation({
    context: { resolveWithUXP: true },
    onError: (mutError) => {
      logger.error('setPlayheadPosition mutation failed', {
        error: mutError.message,
        assetId,
        sequenceId,
      });
    },
  });

  // Premiere → Frame.io: Subscribe to position changes from Premiere
  usePlayheadPositionChangedSubscription({
    context: { resolveWithUXP: true },
    skip: shouldSkip,
    onError: (subscriptionError) => {
      logger.error('Playhead position subscription failed', {
        error: subscriptionError.message,
        assetId,
        sequenceId,
      });
    },
    onData: ({ data }) => {
      const position = data?.data?.playheadPositionChanged;

      if (
        !position ||
        position.sequenceId !== sequenceId ||
        playerInterface.isPlaying()
      ) {
        return;
      }

      const positionSeconds = snapToFrameAccurateTime(
        position,
        mediaData,
        sequenceInOffsetMicroseconds
      );

      if (lastSyncedPositionRef.current !== null) {
        const relativeMicroseconds =
          position.positionMicroseconds - sequenceInOffsetMicroseconds;
        const lastSyncedMicroseconds =
          lastSyncedPositionRef.current * MICROSECONDS_PER_SECOND;

        if (
          isPositionWithinThreshold(
            relativeMicroseconds,
            lastSyncedMicroseconds
          )
        ) {
          return;
        }
      }

      lastSyncedPositionRef.current = positionSeconds;

      playerInterface.setCurrentTimeAndPause(positionSeconds);

      skipNextSyncRef.current = true;
    },
  });

  const sendPositionToPremiere = useCallback(
    async (currentTime: number) => {
      if (shouldSkip) {
        return;
      }

      const currentMicroseconds = currentTime * MICROSECONDS_PER_SECOND;
      const lastSyncedMicroseconds =
        (lastSyncedPositionRef.current ?? 0) * MICROSECONDS_PER_SECOND;

      if (
        isPositionWithinThreshold(currentMicroseconds, lastSyncedMicroseconds)
      ) {
        return;
      }

      try {
        const absoluteMicroseconds =
          currentTime * MICROSECONDS_PER_SECOND + sequenceInOffsetMicroseconds;

        await setPlayheadPosition({
          variables: {
            input: {
              assetId,
              sequenceId,
              positionMicroseconds: absoluteMicroseconds,
            },
          },
          onCompleted: () => {
            lastSyncedPositionRef.current = currentTime;
          },
        });
      } catch (error) {
        logger.error('Failed to sync playhead to Premiere', {
          error: error instanceof Error ? error.message : String(error),
          assetId,
          sequenceId,
          currentTime,
        });
      }
    },
    [
      assetId,
      sequenceId,
      setPlayheadPosition,
      logger,
      shouldSkip,
      sequenceInOffsetMicroseconds,
    ]
  );

  // Frame.io → Premiere: Send position when user pauses or seeks
  useEffect(() => {
    if (shouldSkip || playerInterface.isPlaying()) {
      return;
    }

    // Skip if we just received an update from Premiere
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    const currentTime = playerInterface.current?.currentTime;

    if (
      shouldSuppressOutOfBoundsEcho(
        currentTime,
        lastSyncedPositionRef.current,
        mediaData
      )
    ) {
      return;
    }

    if (currentTime && Number.isFinite(currentTime)) {
      sendPositionToPremiere(currentTime);
    }
  }, [playerInterface, sendPositionToPremiere, shouldSkip, mediaData]);

  return {
    isSyncEnabled,
  };
};
